// Master Apps — conjunto de ícones (porte fiel de tokens.jsx).
// SVG 24×24, stroke currentColor, replicando exatamente o design.
import type { CSSProperties, ReactNode } from 'react';

export interface IconProps {
  size?: number;
  stroke?: number;
  style?: CSSProperties;
  fill?: string;
}

export const Icon = ({
  d,
  size = 16,
  stroke = 1.75,
  style,
  fill = 'none',
}: Readonly<IconProps & { d: ReactNode }>) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill}
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
    aria-hidden="true"
  >
    {d}
  </svg>
);

export const IconAlert = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </>
    }
  />
);
// Informação — o inverso do IconAlert: ponto em cima, haste embaixo.
export const IconInfo = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </>
    }
  />
);
export const IconMenu = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="18" x2="21" y2="18" />
      </>
    }
  />
);
export const IconLock = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </>
    }
  />
);
export const IconSun = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
      </>
    }
  />
);

export const IconMoon = (p: Readonly<IconProps>) => (
  <Icon {...p} d={<path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />} />
);

export const IconClock = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </>
    }
  />
);
export const IconShield = (p: Readonly<IconProps>) => (
  <Icon {...p} d={<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />} />
);
export const IconUsers = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </>
    }
  />
);
export const IconBolt = (p: Readonly<IconProps>) => (
  <Icon {...p} d={<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />} />
);
export const IconArrow = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
      </>
    }
  />
);
export const IconChevronR = (p: Readonly<IconProps>) => (
  <Icon {...p} d={<polyline points="9 18 15 12 9 6" />} />
);
export const IconChevronD = (p: Readonly<IconProps>) => (
  <Icon {...p} d={<polyline points="6 9 12 15 18 9" />} />
);
export const IconSearch = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </>
    }
  />
);
export const IconBell = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </>
    }
  />
);
export const IconGrid = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </>
    }
  />
);
export const IconLayers = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <polygon points="12 2 2 7 12 12 22 7 12 2" />
        <polyline points="2 17 12 22 22 17" />
        <polyline points="2 12 12 17 22 12" />
      </>
    }
  />
);
export const IconActivity = (p: Readonly<IconProps>) => (
  <Icon {...p} d={<polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />} />
);
export const IconServer = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <rect x="2" y="2" width="20" height="8" rx="2" />
        <rect x="2" y="14" width="20" height="8" rx="2" />
        <line x1="6" y1="6" x2="6.01" y2="6" />
        <line x1="6" y1="18" x2="6.01" y2="18" />
      </>
    }
  />
);
export const IconLink = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </>
    }
  />
);
export const IconStore = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M3 9l1-5h16l1 5" />
        <path d="M4 9v11h16V9" />
        <path d="M9 22V12h6v10" />
      </>
    }
  />
);
export const IconGlobe = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </>
    }
  />
);
export const IconFilter = (p: Readonly<IconProps>) => (
  <Icon {...p} d={<polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />} />
);
export const IconRefresh = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <polyline points="23 4 23 10 17 10" />
        <polyline points="1 20 1 14 7 14" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
        <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
      </>
    }
  />
);
export const IconEdit = (p: Readonly<IconProps>) => (
  <Icon {...p} d={<path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />} />
);
export const IconExternal = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        <polyline points="15 3 21 3 21 9" />
        <line x1="10" y1="14" x2="21" y2="3" />
      </>
    }
  />
);
export const IconDownload = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </>
    }
  />
);
export const IconCheck = (p: Readonly<IconProps>) => (
  <Icon {...p} d={<polyline points="20 6 9 17 4 12" />} />
);
export const IconX = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </>
    }
  />
);
export const IconCpu = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <rect x="4" y="4" width="16" height="16" rx="2" />
        <rect x="9" y="9" width="6" height="6" />
        <line x1="9" y1="1" x2="9" y2="4" />
        <line x1="15" y1="1" x2="15" y2="4" />
        <line x1="9" y1="20" x2="9" y2="23" />
        <line x1="15" y1="20" x2="15" y2="23" />
        <line x1="20" y1="9" x2="23" y2="9" />
        <line x1="20" y1="14" x2="23" y2="14" />
        <line x1="1" y1="9" x2="4" y2="9" />
        <line x1="1" y1="14" x2="4" y2="14" />
      </>
    }
  />
);
export const IconWifi = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M5 12.55a11 11 0 0 1 14.08 0" />
        <path d="M1.42 9a16 16 0 0 1 21.16 0" />
        <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
        <line x1="12" y1="20" x2="12.01" y2="20" />
      </>
    }
  />
);
export const IconMic = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" />
      </>
    }
  />
);
export const IconTrend = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
      </>
    }
  />
);
export const IconLogout = (p: Readonly<IconProps>) => (
  <Icon
    {...p}
    d={
      <>
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
      </>
    }
  />
);
